import {inject} from '@angular/core';
import {TranslateHelper} from '@binotel-frontend/bnt-common-components';
import {EAnalyticsEvent, EAnalyticsParam} from '@entities/analytics';
import {AUTHENTICATION_STORAGE_KEY} from '@entities/authentication/authentication.constants';
import {
  EAuthenticationBy,
  EAuthenticationFlowView,
} from '@entities/authentication/authentication.enums';
import {ERouterPages} from '@entities/router/router.enums';
import {ESnackbarType} from '@entities/ui/snackbar';
import {Actions, ofType, ROOT_EFFECTS_INIT} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';
import {ErrorsHelper, SnackbarHelper} from '@shared/helper';
import {AnalyticsHelper} from '@shared/helper/analytics.helper';
import {OdApiErrorResponse, StorageService} from '@shared/services';
import {
  createFunctionalEffect,
  createFunctionalSideEffect,
} from '@shared/utils/effects.helper';
import {BranchActions} from '@state/branch/branch.actions';
import {FavoritesActions} from '@state/favorites';
import {HistoryActions} from '@state/history';
import {RouterActions} from '@state/router';
import {routerSelectors} from '@state/router/router.selectors';
import {SubjectActions} from '@state/subject';
import {mapOdApiActions} from '@utils/map-od-api-actions.function';
import {catchError, concatMap, exhaustMap, map, of, take, tap} from 'rxjs';
import {filter} from 'rxjs/operators';

import {AuthenticationActions} from './authentication.actions';
import {AuthenticationApi} from './authentication.api';

export namespace AuthenticationEffects {
  export const login$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(AuthenticationApi)) =>
      actions$.pipe(
        ofType(AuthenticationActions.login.requested),
        exhaustMap(({payload}) =>
          api.login(payload).pipe(
            concatMap((result) => {
              const resultActions: Action<string>[] = [
                AuthenticationActions.login.succeeded({payload: result.data}),
                AuthenticationActions.setCurrentPhone({
                  payload: {phone: payload.phone},
                }),
              ];

              if (
                'type' in result.data &&
                result.data.type === EAuthenticationBy.Phone
              ) {
                resultActions.push(
                  AuthenticationActions.setViewType({
                    to: EAuthenticationFlowView.Confirmation,
                  })
                );
              }

              return resultActions;
            }),
            catchError((error: OdApiErrorResponse) => {
              const apiError = ErrorsHelper.getApiErrorAsString(
                error.error,
                false
              );
              SnackbarHelper.open(
                apiError || TranslateHelper.translate('common.error-occurred'),
                ESnackbarType.Error
              );
              return of(AuthenticationActions.login.failed(error));
            })
          )
        )
      )
  );

  export const loginConfirm$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(AuthenticationApi)) =>
      actions$.pipe(
        ofType(AuthenticationActions.loginConfirm.requested),
        exhaustMap(({payload}) =>
          api
            .confirmLogin(payload)
            .pipe(mapOdApiActions(AuthenticationActions.loginConfirm))
        )
      )
  );

  export const trackAuthFailed$ = createFunctionalSideEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(AuthenticationActions.loginConfirm.failed),
        tap(() => AnalyticsHelper.sendEvent(EAnalyticsEvent.AuthFailed))
      )
  );

  export const trackAuthSuccess$ = createFunctionalSideEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(AuthenticationActions.loginConfirm.succeeded),
        tap(() => {
          AnalyticsHelper.sendEvent(EAnalyticsEvent.AuthFinished);
          AnalyticsHelper.setUserProperties({
            [EAnalyticsParam.UserType]: 'registered',
          });
        })
      )
  );

  export const notifyAuthorized$ = createFunctionalSideEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(AuthenticationActions.loginConfirm.succeeded),
        tap(() =>
          SnackbarHelper.open(
            TranslateHelper.translate('authentication.authorized')
          )
        )
      )
  );

  export const setTokenToStorage$ = createFunctionalSideEffect(
    (actions$ = inject(Actions), storage = inject(StorageService)) =>
      actions$.pipe(
        ofType(
          AuthenticationActions.login.succeeded,
          AuthenticationActions.loginConfirm.succeeded
        ),
        tap((info) => {
          if (info?.payload && 'token' in info.payload) {
            storage.setItem(AUTHENTICATION_STORAGE_KEY, info.payload.token);
          }
        })
      )
  );

  export const installToken$ = createFunctionalEffect(
    (actions$ = inject(Actions), storage = inject(StorageService)) =>
      actions$.pipe(
        ofType(ROOT_EFFECTS_INIT),
        map(() => storage.getItem<string>(AUTHENTICATION_STORAGE_KEY)),
        tap((token) =>
          AnalyticsHelper.setUserProperties({
            [EAnalyticsParam.UserType]: token ? 'registered' : 'anonymous',
          })
        ),
        filter((token): token is string => !!token),
        map((token) => AuthenticationActions.setToken({token}))
      )
  );

  export const clearLoginInfo$ = createFunctionalEffect(
    (
      actions$ = inject(Actions),
      storage = inject(StorageService),
      store = inject(Store)
    ) =>
      actions$.pipe(
        ofType(AuthenticationActions.logout),
        tap(() => {
          storage.removeItem(AUTHENTICATION_STORAGE_KEY);
          AnalyticsHelper.sendEvent(EAnalyticsEvent.Logout);
          AnalyticsHelper.setUserProperties({
            [EAnalyticsParam.UserType]: 'anonymous',
          });
        }),
        concatMap(() =>
          store.select(routerSelectors.selectUrl).pipe(filter(Boolean), take(1))
        ),
        concatMap((currentUrl) => {
          const actionsToDispatch = [
            SubjectActions.resetFavorites(),
            BranchActions.resetFavorites(),
            FavoritesActions.clearFavorites(),
            HistoryActions.clearHistory(),
          ];

          const protectedRoutes = [
            ERouterPages.Settings,
            ERouterPages.Dashboard,
          ];

          const isProtectedPage = protectedRoutes.some((page) =>
            currentUrl.includes(page)
          );

          if (isProtectedPage) {
            actionsToDispatch.push(
              RouterActions.go({path: [ERouterPages.Welcome]})
            );
          }

          return actionsToDispatch;
        })
      )
  );
}
